import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customContentAPI, verifyPasswordAPI } from "../services/apiService";
import { trackPasswordVerification } from "../utils/trackingUtils";

const TestResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showResult, setShowResult] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [customContent, setCustomContent] = useState({});
  const [isVerifying, setIsVerifying] = useState(false); // Track verify loading state
  const [showInstructions, setShowInstructions] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true); // Track loading state
  const instructionsRef = useRef(null); // Ref for scrolling to instructions section

  // Get test data from navigation state
  const testData = location.state || {};
  const { testType, result, details, breakdown } = testData;

  // Load custom content from database on component mount - CHỈ load content, KHÔNG load password
  useEffect(() => {
    const loadData = async () => {
      setIsContentLoading(true);
      try {
        // Load custom content for instructions - KHÔNG load password vì verify trên server
        const content = await customContentAPI.get();
        setCustomContent(content || {});
      } catch (error) {
        console.error("Failed to load custom content:", error);
      } finally {
        setIsContentLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle click on "hướng dẫn tại đây" - toggle instructions and scroll to it
  const handleToggleInstructions = (show = true) => {
    setShowInstructions(show);
    if (show) {
      // Wait for the instructions section to render, then scroll to it
      setTimeout(() => {
        if (instructionsRef.current) {
          instructionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  // Scroll to top of instructions again after content finishes loading
  useEffect(() => {
    if (showInstructions && !isContentLoading && instructionsRef.current) {
      // Content just finished loading, scroll to top of instructions section
      setTimeout(() => {
        if (instructionsRef.current) {
          instructionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 50);
    }
  }, [isContentLoading, showInstructions]);

  // HTML content renderer component - for TinyMCE WYSIWYG content
  const HTMLRenderer = ({ content }) => {
    if (!content) return null;

    // Create a clean HTML content with proper styling
    const createMarkup = () => {
      // Basic HTML sanitization - in production use a proper sanitizer like DOMPurify
      // Ensure content is a string
      let cleanHtml = typeof content === 'string' ? content : String(content || '');

      // Ensure images are responsive and safe - NO BORDERS, but keep width/height/style if present
      cleanHtml = cleanHtml.replace(/<img([^>]*?)>/g, (match, attrs) => {
        // Parse width/height/style
        let style = attrs.match(/style\s*=\s*['\"]([^'"]*)['\"]/i);
        let newStyle = style ? style[1] : "";
        // Merge with responsive style, but keep width/height if present
        if (!/max-width/.test(newStyle)) newStyle += ";max-width:100%";
        if (!/height/.test(newStyle)) newStyle += ";height:auto";
        newStyle += ";border:none";
        // Remove duplicate style attr
        let attrsNoStyle = attrs.replace(/style\s*=\s*['\"][^'"]*['\"]/i, "");
        // Compose new img tag
        return `<img${attrsNoStyle} style="${newStyle.trim()}" onerror="this.alt='❌ Không thể tải hình ảnh'; this.style.backgroundColor='#f3f4f6';" onload="this.style.backgroundColor='transparent';">`;
      });

      // Ensure links are safe
      cleanHtml = cleanHtml.replace(
        /<a\s/g,
        '<a target="_blank" rel="noopener noreferrer" '
      );

      return { __html: cleanHtml };
    };

    return (
      <div
        className="wysiwyg-content max-w-none"
        style={{
          lineHeight: "1.6",
          color: "#333",
        }}
      >
        <style>{`
           /* Reset borders only - KHÔNG reset margin/padding để giữ text-align */
           .wysiwyg-content * {
             border-left: none !important;
           }

           .wysiwyg-content blockquote {
             border-left: none !important;
             padding-left: 16px !important;
             margin-left: 0 !important;
             font-style: italic;
             background-color: #f9fafb;
             padding: 12px 16px;
             border-radius: 6px;
           }

           /* Preserve text-align from TinyMCE */
           .wysiwyg-content p,
           .wysiwyg-content h1,
           .wysiwyg-content h2,
           .wysiwyg-content h3,
           .wysiwyg-content h4,
           .wysiwyg-content div {
             /* Giữ nguyên text-align từ inline style */
           }

           .wysiwyg-content p {
             margin: 0 0 1em 0;
             line-height: 1.6;
           }
           .wysiwyg-content h1, .wysiwyg-content h2, .wysiwyg-content h3, .wysiwyg-content h4 {
             margin: 1.2em 0 0.6em 0;
             font-weight: bold;
             line-height: 1.4;
           }
           .wysiwyg-content ul, .wysiwyg-content ol {
             padding-left: 20px;
             margin: 0.8em 0;
           }
           .wysiwyg-content a {
             color: #3b82f6;
             text-decoration: underline;
           }
           .wysiwyg-content a:hover {
             color: #1d4ed8;
           }
           
           /* Image alignment fixes - enhanced with NO BORDERS */
           .wysiwyg-content img {
             max-width: 100% !important;
             height: auto !important;
             border: none !important;
             border-radius: 4px;
             margin: 10px 0;
             box-shadow: none !important;
           }
           
           /* Force center alignment - nhiều cách khác nhau */
           .wysiwyg-content img[style*="display: block; margin: 0 auto"],
           .wysiwyg-content img[style*="margin: 0 auto"],
           .wysiwyg-content img[style*="margin:0 auto"], 
           .wysiwyg-content img[style*="margin: auto"],
           .wysiwyg-content img[style*="text-align: center"],
           .wysiwyg-content p[style*="text-align: center"] img,
           .wysiwyg-content div[style*="text-align: center"] img {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
             text-align: center !important;
           }
           
           /* Text alignment - respect TinyMCE inline styles */
           /* Default: left alignment for all text elements */
           .wysiwyg-content p,
           .wysiwyg-content div,
           .wysiwyg-content h1,
           .wysiwyg-content h2,
           .wysiwyg-content h3,
           .wysiwyg-content h4 {
             text-align: left;
           }

           /* Override with explicit alignment from TinyMCE */
           .wysiwyg-content p[style*="text-align: left"],
           .wysiwyg-content div[style*="text-align: left"],
           .wysiwyg-content h1[style*="text-align: left"],
           .wysiwyg-content h2[style*="text-align: left"],
           .wysiwyg-content h3[style*="text-align: left"],
           .wysiwyg-content h4[style*="text-align: left"] {
             text-align: left !important;
           }

           .wysiwyg-content p[style*="text-align: center"],
           .wysiwyg-content div[style*="text-align: center"],
           .wysiwyg-content h1[style*="text-align: center"],
           .wysiwyg-content h2[style*="text-align: center"],
           .wysiwyg-content h3[style*="text-align: center"],
           .wysiwyg-content h4[style*="text-align: center"] {
             text-align: center !important;
           }

           .wysiwyg-content p[style*="text-align: right"],
           .wysiwyg-content div[style*="text-align: right"],
           .wysiwyg-content h1[style*="text-align: right"],
           .wysiwyg-content h2[style*="text-align: right"],
           .wysiwyg-content h3[style*="text-align: right"],
           .wysiwyg-content h4[style*="text-align: right"] {
             text-align: right !important;
           }

           .wysiwyg-content p[style*="text-align: justify"],
           .wysiwyg-content div[style*="text-align: justify"],
           .wysiwyg-content h1[style*="text-align: justify"],
           .wysiwyg-content h2[style*="text-align: justify"],
           .wysiwyg-content h3[style*="text-align: justify"],
           .wysiwyg-content h4[style*="text-align: justify"] {
             text-align: justify !important;
           }

           /* Force left alignment for images */
           .wysiwyg-content img[style*="float: left"] {
             float: left !important;
             margin: 0 15px 15px 0 !important;
           }

           /* Force right alignment for images */
           .wysiwyg-content img[style*="float: right"] {
             float: right !important;
             margin: 0 0 15px 15px !important;
           }
           
           /* REMOVED: This was breaking text-align center */
           /* Do NOT reset margin-left/padding-left globally */
           
           /* FORCE center alignment - highest specificity */
           .wysiwyg-content [style*="text-align: center"] img,
           .wysiwyg-content img[style*="margin: 0 auto"],
           .wysiwyg-content img[style*="margin-left: auto"],
           .wysiwyg-content img[style*="margin-right: auto"],
           .wysiwyg-content img[style*="display: block; margin-left: auto; margin-right: auto"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
           }
           
           /* Override parent paragraph text-align when img has center margins */
           .wysiwyg-content p[style*="text-align: left"] img[style*="margin-left: auto"],
           .wysiwyg-content p[style*="text-align: left"] img[style*="margin-right: auto"],
           .wysiwyg-content p[style*="text-align: left"] span img[style*="margin-left: auto"],
           .wysiwyg-content p[style*="text-align: left"] span img[style*="margin-right: auto"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
             text-align: center !important;
           }
           
           /* Force specific class images to center regardless of parent */
           .wysiwyg-content img.iatup_getpass_img,
           .wysiwyg-content img.iatup_keyword_image {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
             text-align: center !important;
           }
           
           /* Force all images with auto margins to center - ULTIMATE RULE */
           .wysiwyg-content img[style*="auto"],
           .wysiwyg-content * img[style*="auto"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
           }
           
           /* Ultimate CSS force for center alignment */
           .wysiwyg-content img[style*="margin-left: auto"][style*="margin-right: auto"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
             width: auto !important;
             max-width: 100% !important;
           }
           
           /* NUCLEAR OPTION: Force ALL images to center if they have any auto margin */
           .wysiwyg-content img[style*="auto"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;  
             margin-right: auto !important;
             text-align: center !important;
             clear: both !important;
           }
           
           /* Fallback: Force the specific problematic image */
           .wysiwyg-content img[data-src*="getpass"],
           .wysiwyg-content img[src*="getpass"] {
             display: block !important;
             margin: 10px auto !important;
             margin-left: auto !important;
             margin-right: auto !important;
           }
         `}</style>
        <div
          style={{
            borderLeft: "none",
            paddingLeft: "0",
            marginLeft: "0",
          }}
          dangerouslySetInnerHTML={createMarkup()}
        />
      </div>
    );
  };

  // Validate test data and redirect if invalid
  useEffect(() => {
    if (!testData || !testType) {
      navigate("/");
      return;
    }
  }, [testData, testType, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Check if password is empty
    if (!password.trim()) {
      return;
    }

    // Prevent double-submit
    if (isVerifying) return;
    setIsVerifying(true);

    try {
      // ✅ SECURE: Verify password trên server - KHÔNG load password về frontend
      const result = await verifyPasswordAPI.verify(testType, password);
      console.log('🔐 Password verification result:', result.success ? '✅' : '❌');

      if (result.success) {
        setShowResult(true);

        // Track successful password verification
        try {
          await trackPasswordVerification(testType);
          console.log('✅ Password verification tracked');
        } catch (error) {
          console.error('❌ Failed to track password verification:', error);
        }
      } else {
        // Show error modal
        setShowErrorModal(true);
        setPassword("");
      }
    } catch (error) {
      console.error('❌ Password verification error:', error);
      setShowErrorModal(true);
      setPassword("");
    } finally {
      setIsVerifying(false);
    }
  };

  const getTestTitle = (type) => {
    const titles = {
      mbti: "Test MBTI",
      iq: "Test IQ",
      eq: "Test EQ",
      disc: "Test DISC",
      holland: "Test Holland Codes",
    };
    return titles[type] || "Test Tâm Lý";
  };

  const getCustomInstructions = (type) => {
    const section = customContent?.[type];
    if (!section) return getDefaultInstructions(type);

    // New multi-template structure support
    if (section.templates && typeof section.templates === 'object') {
      const templateIds = Object.keys(section.templates);
      if (templateIds.length > 0) {
        const activeId = section.activeTemplateId || templateIds[0];
        return section.templates?.[activeId]?.instructions || getDefaultInstructions(type);
      }
    }

    // Backward compatible (single content)
    return section.instructions || getDefaultInstructions(type);
  };

  const getDefaultInstructions = (type) => {
    const defaults = {
      mbti: `# Chúc mừng! Bạn đã hoàn thành Test MBTI

Kết quả test MBTI của bạn đã sẵn sàng! Nhập mật khẩu để xem kết quả chi tiết về tính cách và phong cách làm việc của bạn.`,

      iq: `# Chúc mừng! Bạn đã hoàn thành Test IQ

Kết quả test IQ của bạn đã sẵn sàng! Nhập mật khẩu để xem điểm IQ và phân tích chi tiết về khả năng tư duy của bạn.`,

      eq: `# Chúc mừng! Bạn đã hoàn thành Test EQ

Kết quả test EQ của bạn đã sẵn sàng! Nhập mật khẩu để xem khả năng quản lý cảm xúc và kỹ năng giao tiếp của bạn.`,

      disc: `# Chúc mừng! Bạn đã hoàn thành Test DISC

Kết quả test DISC của bạn đã sẵn sàng! Nhập mật khẩu để xem phân tích chi tiết về phong cách hành vi và cách làm việc của bạn.`,

      holland: `# Chúc mừng! Bạn đã hoàn thành Test Holland Codes

Kết quả test Holland của bạn đã sẵn sàng! Nhập mật khẩu để xem những ngành nghề phù hợp với tính cách và sở thích của bạn.`,
    };
    return (
      defaults[type] || "Vui lòng nhập mật khẩu để xem kết quả test của bạn."
    );
  };


  // Step 1: Content + Password Input
  if (!showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>

            <div className="relative z-10">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-3xl mx-auto mb-4 shadow-lg">
                  ✓
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  Chúc mừng! Bạn đã hoàn thành bài test!
                </h2>
              </div>

              {/* Password Form */}
              <form
                onSubmit={handlePasswordSubmit}
                className="max-w-md mx-auto mb-8"
              >
                <div className="mb-6">
                  <label className="block text-lg font-semibold text-gray-700 mb-3 text-center">
                    <span className="text-orange-600 font-bold text-xl">BƯỚC CUỐI CÙNG!</span>
                    <br />
                    <span className="text-gray-600 mt-2 block">
                      Nhập mật khẩu ở{" "}
                      <button
                        type="button"
                        onClick={() => handleToggleInstructions(!showInstructions)}
                        className="text-blue-600 hover:text-blue-800 font-bold underline cursor-pointer transition-colors"
                      >
                        hướng dẫn tại đây
                      </button>{" "}
                      để xem kết quả
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      required
                      className="w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 pr-12"
                      placeholder="Nhập mật khẩu từ hướng dẫn..."
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!password.trim()}
                  className={`w-full font-bold py-4 px-6 rounded-xl transition-all duration-300 text-lg shadow-lg ${
                    password.trim()
                      ? "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white transform hover:scale-105 cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {password.trim() ? "🎉 Tiếp tục xem kết quả" : "Nhập mật khẩu để tiếp tục"}
                </button>
              </form>

              {/* Custom Content - Show when user clicks the link, with loading state support */}
              {showInstructions && (
                <div
                  ref={instructionsRef}
                  className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      📋 Hướng dẫn lấy mật khẩu
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowInstructions(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                    >
                      ✕
                    </button>
                  </div>
                  {isContentLoading ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                      <p className="text-gray-600 text-center">Đang tải hướng dẫn...</p>
                    </div>
                  ) : (
                    <HTMLRenderer content={getCustomInstructions(testType)} />
                  )}
                </div>
              )}

              {/* Back to Home */}
              <div className="text-center mt-8">
                <button
                  onClick={() => navigate("/")}
                  className="text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-300 flex items-center justify-center mx-auto"
                >
                  ← Quay về trang chủ
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Modal - Wrong Password */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
              {/* Icon - Tinh tế hơn */}
              <div className="flex justify-center mb-6">
                <div className="bg-red-50 rounded-full p-3">
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-3">
                Mật khẩu không chính xác
              </h3>

              {/* Message */}
              <p className="text-gray-600 text-center mb-6">
                Vui lòng xem{" "}
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    handleToggleInstructions(true);
                  }}
                  className="text-blue-600 hover:text-blue-800 font-bold underline cursor-pointer transition-colors"
                >
                  hướng dẫn tại đây
                </button>{" "}
                để lấy mật khẩu chính xác.
              </p>

              {/* Close Button */}
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Step 2: Show Results
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-green-100 to-blue-100 rounded-full -translate-y-20 translate-x-20 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full translate-y-16 -translate-x-16 opacity-50"></div>

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                Kết quả {getTestTitle(testType)}
              </h1>
              <p className="text-gray-600 text-lg">
                Chúc mừng! Đây là kết quả chi tiết của bạn
              </p>
            </div>

            {/* Result Content */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-8 border border-blue-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Kết quả của bạn
                  </h3>
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {typeof result === "object"
                        ? JSON.stringify(result)
                        : result}
                    </div>
                    {(breakdown || details) && (
                      <div className="text-gray-700 leading-relaxed">
                        {breakdown ? (
                          <div className="whitespace-pre-line text-sm">
                            {breakdown}
                          </div>
                        ) : typeof details === "object" ? (
                          <pre className="whitespace-pre-wrap text-sm bg-gray-100 p-4 rounded-lg">
                            {JSON.stringify(details, null, 2)}
                          </pre>
                        ) : (
                          <p>{details}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Gợi ý cho bạn
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-green-500">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Điểm mạnh
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Bạn có những đặc điểm tích cực cần phát huy trong cuộc
                        sống và công việc.
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-md border-l-4 border-blue-500">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Hướng phát triển
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Tập trung vào những kỹ năng này để nâng cao hiệu quả cá
                        nhân.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {testType === "iq" && (
                <button
                  onClick={() => setShowAnswers(true)}
                  className="bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  📝 Xem đáp án
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Về trang chủ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Answers Modal - For IQ Test */}
      {showAnswers && testType === "iq" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">📝</span>
                <h3 className="text-2xl font-bold">Đáp án Test IQ</h3>
              </div>
              <button
                onClick={() => setShowAnswers(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-8 max-h-[70vh] overflow-y-auto">
              {customContent.iq_answers?.instructions ? (
                <HTMLRenderer content={customContent.iq_answers.instructions} />
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📚</div>
                  <p className="text-gray-600 text-lg">
                    Đáp án chưa được cập nhật.
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Vui lòng liên hệ quản trị viên để được hỗ trợ.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end">
              <button
                onClick={() => setShowAnswers(false)}
                className="bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestResult;
